import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Button, Spinner, Alert, Badge, Table, Form, Tabs, Tab, Modal, Breadcrumb } from 'react-bootstrap';
import { ArrowLeft } from 'lucide-react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { officeService, deskService, carParkPlanService, companyService } from '../services/apiService';
import { usePermissions } from '../context/PermissionsContext';
import { useAuthToken } from '../hooks/useAuthToken';
import { LoadingState, ErrorState, isNotFound, serverMessage } from '../components/PageState';
import { formatNumber, plural, EMPTY } from '../utils/format';
import { rowClick } from '../utils/rows';

const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';

// An SVG shown through an img never runs its scripts or event handlers, unlike markup inserted into
// the page. An img also needs the SVG namespace, which hand-written markup sometimes leaves out.
const svgDataUri = (svg) => {
  const markup = /<svg\b[^>]*\bxmlns\s*=/i.test(svg) ? svg : svg.replace(/<svg\b/i, `<svg xmlns="${SVG_NAMESPACE}"`);
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(markup)}`;
};

const SvgPreview = ({ svg, alt, maxHeight = 140 }) => {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [svg]);
  if (failed) return <span className="text-muted small">Preview not available</span>;
  return (
    <img
      src={svgDataUri(svg)}
      alt={alt}
      onError={() => setFailed(true)}
      style={{ maxWidth: '100%', maxHeight, height: 'auto' }}
    />
  );
};

/**
 * An SVG plan field: drop a file on the box, choose one with the button, or paste the markup below.
 * Used for both the floor's office plan and car park maps.
 */
const SvgFileField = ({ id, label, value, onChange, disabled = false }) => {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [fileError, setFileError] = useState(null);

  const readFile = (file) => {
    if (!file) return;
    if (!/\.svg$/i.test(file.name) && file.type !== 'image/svg+xml') {
      setFileError('Choose an SVG file.');
      return;
    }
    setFileError(null);
    const reader = new FileReader();
    reader.onload = (event) => onChange(event.target.result);
    reader.onerror = () => setFileError('Could not read that file.');
    reader.readAsText(file);
  };

  return (
    <Form.Group className="mb-3">
      <Form.Label htmlFor={`${id}-markup`}>{label}</Form.Label>
      <div
        onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          if (!disabled) readFile(e.dataTransfer.files[0]);
        }}
        className="d-flex flex-column align-items-center gap-2 p-3 text-center"
        style={{
          border: `2px dashed ${dragging ? 'var(--color-accent-emphasis)' : 'var(--border-strong)'}`,
          borderRadius: '6px',
          transition: 'border-color 0.2s',
        }}
      >
        {value ? (
          <SvgPreview svg={value} alt={`${label} preview`} />
        ) : (
          <span className="text-muted">Drag an SVG file here, choose one, or paste the markup below</span>
        )}
        <div className="d-flex flex-wrap justify-content-center gap-2">
          <Button variant="outline-secondary" size="sm" onClick={() => inputRef.current?.click()} disabled={disabled}>
            Choose SVG file
          </Button>
          {value && (
            <Button variant="link" size="sm" className="text-danger" onClick={() => onChange('')} disabled={disabled}>
              Remove
            </Button>
          )}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept=".svg,image/svg+xml"
          className="d-none"
          tabIndex={-1}
          disabled={disabled}
          onChange={(e) => {
            readFile(e.target.files[0]);
            // Choosing the same file again should still load it
            e.target.value = '';
          }}
        />
      </div>
      {fileError && <div className="small text-danger mt-1" role="alert">{fileError}</div>}
      <Form.Control
        id={`${id}-markup`}
        as="textarea"
        rows={4}
        placeholder="Or paste the SVG markup here"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="mt-2"
        style={{ fontFamily: 'monospace', fontSize: '12px' }}
      />
    </Form.Group>
  );
};

// The Settings tab's fields, normalised so an untouched form never reads as changed
const settingsOf = (office) => ({
  hasCarParkPlan: !!office.hasCarParkPlan,
  carParkCapacity: office.carParkCapacity ?? null,
  officePlan: office.officePlan || '',
});

const officeType = (office) => {
  if (office.isOffice) return 'Office';
  if (office.isFloor) return office.hasAreas ? 'Floor with areas' : 'Floor';
  if (office.isArea) return 'Area';
  return EMPTY;
};

const onOff = (value) => (value ? 'On' : 'Off');

const OfficeDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const refreshToken = useAuthToken();
  const { canEdit } = usePermissions();
  const [office, setOffice] = useState(null);
  const [childOffices, setChildOffices] = useState([]);
  const [desks, setDesks] = useState([]);
  const [carParkPlans, setCarParkPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [childOfficesLoading, setChildOfficesLoading] = useState(false);
  const [childOfficesError, setChildOfficesError] = useState(null);
  const [desksLoading, setDesksLoading] = useState(false);
  const [desksError, setDesksError] = useState(null);
  const [carParkPlansLoading, setCarParkPlansLoading] = useState(false);
  const [carParkPlansError, setCarParkPlansError] = useState(null);
  const [newPlanName, setNewPlanName] = useState('');
  const [newPlanMap, setNewPlanMap] = useState('');
  const [isCreatingPlan, setIsCreatingPlan] = useState(false);
  const [planSaved, setPlanSaved] = useState(false);
  const [createPlanError, setCreatePlanError] = useState(null);
  const [showAddPlanModal, setShowAddPlanModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [editedOffice, setEditedOffice] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [showAddFloorModal, setShowAddFloorModal] = useState(false);
  const [newFloorLabel, setNewFloorLabel] = useState('');
  const [newFloorCapacity, setNewFloorCapacity] = useState('');
  const [isCreatingFloor, setIsCreatingFloor] = useState(false);
  const [createFloorError, setCreateFloorError] = useState(null);
  const [ancestors, setAncestors] = useState([]);
  const [company, setCompany] = useState(null);
  const [showAddDeskModal, setShowAddDeskModal] = useState(false);
  const [newDeskLabel, setNewDeskLabel] = useState('');
  const [isCreatingDesk, setIsCreatingDesk] = useState(false);
  const [createDeskError, setCreateDeskError] = useState(null);

  useEffect(() => {
    // Another office may not have the tab that was open
    setActiveTab('overview');
    setSaveStatus(null);
    setPlanSaved(false);
    fetchOffice();
    fetchChildOffices();
    fetchDesks();
    fetchCarParkPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (office) {
      setEditedOffice({ ...office });
      fetchAncestors(office);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [office]);

  const handleFieldChange = (field, value) => {
    setSaveStatus(null);
    setEditedOffice((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setSaveStatus(null);
      await refreshToken();
      const response = await officeService.updateOffice(id, editedOffice);
      setOffice(response.data);
      setSaveStatus({ type: 'success', message: 'Saved' });
    } catch (err) {
      setSaveStatus({ type: 'error', message: serverMessage(err, 'Could not save the settings. Try again.') });
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  const fetchOffice = async () => {
    try {
      setLoading(true);
      setLoadError(null);
      await refreshToken();
      const response = await officeService.getOffice(id);
      setOffice(response.data);
    } catch (err) {
      setOffice(null);
      setLoadError(err);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchChildOffices = async () => {
    try {
      setChildOfficesLoading(true);
      setChildOfficesError(null);
      await refreshToken();
      const response = await officeService.getOfficesByParent(id);
      setChildOffices(response.data);
    } catch (err) {
      setChildOfficesError(err);
      console.error('Failed to load child offices:', err);
    } finally {
      setChildOfficesLoading(false);
    }
  };

  const fetchDesks = async () => {
    try {
      setDesksLoading(true);
      setDesksError(null);
      await refreshToken();
      const response = await deskService.getDesksByOffice(id);
      setDesks(response.data);
    } catch (err) {
      setDesksError(err);
      console.error('Failed to load desks:', err);
    } finally {
      setDesksLoading(false);
    }
  };

  const fetchCarParkPlans = async () => {
    try {
      setCarParkPlansLoading(true);
      setCarParkPlansError(null);
      await refreshToken();
      const response = await carParkPlanService.getCarParkPlansByOffice(id);
      setCarParkPlans(response.data);
    } catch (err) {
      setCarParkPlansError(err);
      console.error('Failed to load car park plans:', err);
    } finally {
      setCarParkPlansLoading(false);
    }
  };

  const fetchAncestors = async (currentOffice) => {
    try {
      await refreshToken();
      const chain = [];
      let parentId = currentOffice.parentId;
      while (parentId) {
        const response = await officeService.getOffice(parentId);
        chain.unshift(response.data);
        parentId = response.data.parentId;
      }
      setAncestors(chain);
      if (currentOffice.companyId) {
        const companyResponse = await companyService.getCompany(currentOffice.companyId);
        setCompany(companyResponse.data);
      }
    } catch (err) {
      console.error('Failed to load ancestors:', err);
    }
  };

  const openAddPlanModal = () => {
    setEditingPlan(null);
    setNewPlanName('');
    setNewPlanMap('');
    setCreatePlanError(null);
    setPlanSaved(false);
    setShowAddPlanModal(true);
  };

  const openEditPlanModal = (plan) => {
    setEditingPlan(plan);
    setNewPlanName(plan.name || '');
    setNewPlanMap(plan.carParkMap || '');
    setCreatePlanError(null);
    setPlanSaved(false);
    setShowAddPlanModal(true);
  };

  const closePlanModal = () => {
    setShowAddPlanModal(false);
    setEditingPlan(null);
    setNewPlanName('');
    setNewPlanMap('');
    setCreatePlanError(null);
  };

  const closeFloorModal = () => {
    setShowAddFloorModal(false);
    setNewFloorLabel('');
    setNewFloorCapacity('');
    setCreateFloorError(null);
  };

  const handleCreateFloor = async () => {
    if (!newFloorLabel.trim()) {
      setCreateFloorError('Please provide a floor name');
      return;
    }

    try {
      setIsCreatingFloor(true);
      setCreateFloorError(null);

      await refreshToken();

      await officeService.createOffice({
        label: newFloorLabel,
        capacity: newFloorCapacity ? parseInt(newFloorCapacity) : null,
        parentId: parseInt(id),
        companyId: office.companyId,
        isFloor: true,
        isOffice: false,
        isArea: false,
        hasAreas: false,
        active: true,
      });

      closeFloorModal();
      await fetchChildOffices();
    } catch (err) {
      setCreateFloorError(`Could not add the floor: ${serverMessage(err, err.message)}`);
      console.error(err);
    } finally {
      setIsCreatingFloor(false);
    }
  };

  const closeDeskModal = () => {
    setShowAddDeskModal(false);
    setNewDeskLabel('');
    setCreateDeskError(null);
  };

  const handleCreateDesk = async () => {
    if (!newDeskLabel.trim()) {
      setCreateDeskError('Please provide a desk label');
      return;
    }

    try {
      setIsCreatingDesk(true);
      setCreateDeskError(null);

      await refreshToken();

      await deskService.createDesk(id, {
        label: newDeskLabel,
        active: true,
      });

      closeDeskModal();
      await fetchDesks();
    } catch (err) {
      setCreateDeskError(`Could not add the desk: ${serverMessage(err, err.message)}`);
      console.error(err);
    } finally {
      setIsCreatingDesk(false);
    }
  };

  const handleSaveCarParkPlan = async () => {
    if (!newPlanName.trim()) {
      setCreatePlanError('Please provide a name');
      return;
    }

    try {
      setIsCreatingPlan(true);
      setCreatePlanError(null);
      setPlanSaved(false);

      await refreshToken();

      const data = {
        name: newPlanName,
        carParkMap: newPlanMap || null,
      };

      if (editingPlan) {
        await carParkPlanService.updateCarParkPlan(editingPlan.id, data);
      } else {
        await carParkPlanService.createCarParkPlan(id, data);
      }

      closePlanModal();
      setPlanSaved(true);

      await fetchCarParkPlans();
    } catch (err) {
      setCreatePlanError(`Could not save the car park plan: ${serverMessage(err, err.message)}`);
      console.error(err);
    } finally {
      setIsCreatingPlan(false);
    }
  };

  if (loading || !office) {
    return (
      <Container fluid className="py-4">
        <div className="mb-3">
          <Button variant="outline-secondary" onClick={() => navigate(-1)}>
            <ArrowLeft size={14} style={{ verticalAlign: '-2px', marginRight: 4 }} />Back
          </Button>
        </div>
        {loading ? (
          <LoadingState label="Loading office" />
        ) : isNotFound(loadError) ? (
          <Alert variant="warning">Office not found</Alert>
        ) : (
          <ErrorState message={serverMessage(loadError, 'Could not load this office.')} onRetry={fetchOffice} />
        )}
      </Container>
    );
  }

  const childNoun = office.isOffice ? 'Floors' : 'Areas';
  const showChildren = office.isOffice || office.isFloor || childOffices.length > 0;
  const hasSettings = office.isOffice || office.isFloor;
  const settingsDirty = JSON.stringify(settingsOf(editedOffice)) !== JSON.stringify(settingsOf(office));

  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col>
          <Breadcrumb>
            {company && (
              <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/companies/${company.id}?tab=offices` }}>
                {company.name}
              </Breadcrumb.Item>
            )}
            {ancestors.map((ancestor) => (
              <Breadcrumb.Item key={ancestor.id} linkAs={Link} linkProps={{ to: `/offices/${ancestor.id}` }}>
                {ancestor.label}
              </Breadcrumb.Item>
            ))}
            <Breadcrumb.Item active>{office.label}</Breadcrumb.Item>
          </Breadcrumb>
          <div className="d-flex flex-wrap align-items-center gap-3">
            <h1 className="mb-0">{office.label}</h1>
            <Badge bg={office.active ? 'success' : 'secondary'}>
              {office.active ? 'Active' : 'Inactive'}
            </Badge>
          </div>
        </Col>
      </Row>

      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-4">
        <Tab eventKey="overview" title="Overview">
          <Row className="g-4 mb-4">
            <Col lg={6}>
              <Card className="shadow-sm h-100">
                <Card.Header className="bg-light">
                  <h5 className="mb-0">Details</h5>
                </Card.Header>
                <Card.Body>
                  <dl className="kv-list">
                    <dt>Type</dt>
                    <dd>{officeType(office)}</dd>
                    <dt>ID</dt>
                    <dd><code>{office.id}</code></dd>
                    <dt>Postcode</dt>
                    <dd>{office.postCode || EMPTY}</dd>
                    <dt>Capacity</dt>
                    <dd>{formatNumber(office.capacity || 0)}</dd>
                    <dt>Desk booking</dt>
                    <dd>{onOff(office.deskBookingEnabled)}</dd>
                    <dt>Floor plan</dt>
                    <dd>{onOff(office.floorPlanEnabled)}</dd>
                    <dt>Car parking</dt>
                    <dd>
                      {office.hasCarParkPlan && office.carParkCapacity > 0
                        ? `On, ${plural(office.carParkCapacity, 'space')}`
                        : onOff(office.hasCarParkPlan)}
                    </dd>
                  </dl>
                </Card.Body>
              </Card>
            </Col>

            {office.floorPlanEnabled && office.floorPlanImageUrl && (
              <Col lg={6}>
                <Card className="shadow-sm h-100">
                  <Card.Header className="bg-light">
                    <h5 className="mb-0">Floor plan</h5>
                  </Card.Header>
                  <Card.Body>
                    <div className="text-center">
                      <img
                        src={office.floorPlanImageUrl}
                        alt={`Floor plan of ${office.label}`}
                        style={{ maxWidth: '100%', height: 'auto' }}
                        className="img-fluid"
                      />
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            )}
          </Row>

          {showChildren && (
            <Row className="mb-4">
              <Col>
                <Card className="shadow-sm">
                  <Card.Header className="bg-light d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">{childNoun} ({childOffices.length})</h5>
                    {canEdit && office.isOffice && (
                      <Button variant="primary" size="sm" onClick={() => setShowAddFloorModal(true)}>
                        Add floor
                      </Button>
                    )}
                  </Card.Header>
                  <Card.Body className="p-0">
                    {childOfficesLoading ? (
                      <LoadingState label={`Loading ${childNoun.toLowerCase()}`} />
                    ) : childOfficesError ? (
                      <ErrorState
                        inCard
                        message={`Could not load the ${childNoun.toLowerCase()}.`}
                        onRetry={fetchChildOffices}
                      />
                    ) : childOffices.length === 0 ? (
                      <div className="p-4 text-center text-muted">
                        {office.isOffice ? 'No floors yet' : 'No areas on this floor'}
                      </div>
                    ) : (
                      <Table responsive hover className="mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Name</th>
                            <th className="d-none d-md-table-cell">Postcode</th>
                            <th className="text-center">Capacity</th>
                            <th className="text-center">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {childOffices.map((childOffice) => (
                            <tr
                              key={childOffice.id}
                              onClick={rowClick(() => navigate(`/offices/${childOffice.id}`))}
                              style={{ cursor: 'pointer' }}
                              className="table-row-hover"
                            >
                              <td className="align-middle">
                                <Link className="cell-link" to={`/offices/${childOffice.id}`}>{childOffice.label}</Link>
                              </td>
                              <td className="align-middle text-nowrap d-none d-md-table-cell">{childOffice.postCode || EMPTY}</td>
                              <td className="text-center align-middle">
                                {formatNumber(childOffice.capacity || 0)}
                              </td>
                              <td className="text-center align-middle">
                                <Badge bg={childOffice.active ? 'success' : 'secondary'}>
                                  {childOffice.active ? 'Active' : 'Inactive'}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          {canEdit && (
            <Modal show={showAddFloorModal} onHide={closeFloorModal} centered>
              <Modal.Header closeButton>
                <Modal.Title>Add floor</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                {createFloorError && (
                  <Alert variant="danger" dismissible onClose={() => setCreateFloorError(null)}>
                    {createFloorError}
                  </Alert>
                )}

                <Form>
                  <Form.Group className="mb-3" controlId="newFloorLabel">
                    <Form.Label>Floor name</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter floor name"
                      value={newFloorLabel}
                      onChange={(e) => setNewFloorLabel(e.target.value)}
                      disabled={isCreatingFloor}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3" controlId="newFloorCapacity">
                    <Form.Label>Capacity</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      placeholder="Enter capacity (optional)"
                      value={newFloorCapacity}
                      onChange={(e) => setNewFloorCapacity(e.target.value)}
                      disabled={isCreatingFloor}
                    />
                  </Form.Group>
                </Form>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="outline-secondary" onClick={closeFloorModal} disabled={isCreatingFloor}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleCreateFloor}
                  disabled={isCreatingFloor || !newFloorLabel.trim()}
                >
                  {isCreatingFloor ? (
                    <>
                      <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                      Adding...
                    </>
                  ) : (
                    'Add floor'
                  )}
                </Button>
              </Modal.Footer>
            </Modal>
          )}

          {(office.isFloor || office.deskBookingEnabled) && (
            <Row>
              <Col>
                <Card className="shadow-sm">
                  <Card.Header className="bg-light d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Desks ({desks.length})</h5>
                    {canEdit && office.isFloor && (
                      <Button variant="primary" size="sm" onClick={() => setShowAddDeskModal(true)}>
                        Add desk
                      </Button>
                    )}
                  </Card.Header>
                  <Card.Body className="p-0">
                    {desksLoading ? (
                      <LoadingState label="Loading desks" />
                    ) : desksError ? (
                      <ErrorState inCard message="Could not load the desks." onRetry={fetchDesks} />
                    ) : desks.length === 0 ? (
                      <div className="p-4 text-center text-muted">
                        No desks on this floor yet
                      </div>
                    ) : (
                      <Table responsive hover className="mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Desk</th>
                            <th className="text-center">Status</th>
                            <th className="text-center d-none d-md-table-cell">Position (x, y)</th>
                            <th className="text-center d-none d-md-table-cell">Size (w × h)</th>
                            <th className="text-center d-none d-md-table-cell">Rotation</th>
                          </tr>
                        </thead>
                        <tbody>
                          {desks.map((desk) => (
                            <tr key={desk.id}>
                              <td className="align-middle">
                                <strong>{desk.label}</strong>
                              </td>
                              <td className="text-center align-middle">
                                <Badge bg={desk.active ? 'success' : 'secondary'}>
                                  {desk.active ? 'Active' : 'Inactive'}
                                </Badge>
                              </td>
                              <td className="text-center align-middle d-none d-md-table-cell">
                                ({desk.x || 0}, {desk.y || 0})
                              </td>
                              <td className="text-center align-middle d-none d-md-table-cell">
                                {desk.width || 0} × {desk.height || 0}
                              </td>
                              <td className="text-center align-middle d-none d-md-table-cell">
                                {desk.rotation || 0}°
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          {canEdit && (
            <Modal show={showAddDeskModal} onHide={closeDeskModal} centered>
              <Modal.Header closeButton>
                <Modal.Title>Add desk</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                {createDeskError && (
                  <Alert variant="danger" dismissible onClose={() => setCreateDeskError(null)}>
                    {createDeskError}
                  </Alert>
                )}

                <Form>
                  <Form.Group className="mb-3" controlId="newDeskLabel">
                    <Form.Label>Desk label</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter desk label"
                      value={newDeskLabel}
                      onChange={(e) => setNewDeskLabel(e.target.value)}
                      disabled={isCreatingDesk}
                    />
                  </Form.Group>
                </Form>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="outline-secondary" onClick={closeDeskModal} disabled={isCreatingDesk}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleCreateDesk}
                  disabled={isCreatingDesk || !newDeskLabel.trim()}
                >
                  {isCreatingDesk ? (
                    <>
                      <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                      Adding...
                    </>
                  ) : (
                    'Add desk'
                  )}
                </Button>
              </Modal.Footer>
            </Modal>
          )}
        </Tab>

        {/* Areas have nothing to set here, so they get no Settings tab */}
        {hasSettings && (
          <Tab eventKey="settings" title="Settings">
            <Card className="shadow-sm">
              <Card.Header className="bg-light">
                <h5 className="mb-0">Settings</h5>
              </Card.Header>
              <Card.Body>
                <Form onSubmit={(e) => { e.preventDefault(); if (canEdit && settingsDirty && !isSaving) handleSave(); }}>
                  <fieldset disabled={!canEdit || isSaving}>
                    {office.isOffice && (
                      <>
                        <h6 className="text-muted mb-3">Car parking</h6>
                        <Row className="mb-3">
                          <Col md={4}>
                            <Form.Check
                              type="switch"
                              id="carParkingEnabled"
                              label="Enable car parking"
                              checked={editedOffice.hasCarParkPlan || false}
                              onChange={(e) => handleFieldChange('hasCarParkPlan', e.target.checked)}
                            />
                            <Form.Text className="text-muted">
                              Allow users to book car parking spaces at this office
                            </Form.Text>
                          </Col>
                        </Row>
                        {editedOffice.hasCarParkPlan && (
                          <Row className="mb-3">
                            <Col md={4}>
                              <Form.Group className="mb-3" controlId="carParkCapacity">
                                <Form.Label>Car park capacity</Form.Label>
                                <Form.Control
                                  type="number"
                                  min="0"
                                  value={editedOffice.carParkCapacity ?? ''}
                                  onChange={(e) => handleFieldChange('carParkCapacity', e.target.value ? parseInt(e.target.value) : null)}
                                  placeholder="Number of spaces"
                                />
                                <Form.Text className="text-muted">
                                  Total number of parking spaces available
                                </Form.Text>
                              </Form.Group>
                            </Col>
                          </Row>
                        )}
                      </>
                    )}

                    {office.isFloor && (
                      <Row className="mb-3">
                        <Col md={8}>
                          <SvgFileField
                            id="officePlan"
                            label="Office plan"
                            value={editedOffice.officePlan || ''}
                            onChange={(value) => handleFieldChange('officePlan', value)}
                            disabled={!canEdit || isSaving}
                          />
                        </Col>
                      </Row>
                    )}
                  </fieldset>

                  {canEdit && (
                    <>
                      <hr />
                      <div className="d-flex flex-wrap align-items-center gap-3">
                        <Button type="submit" variant="primary" disabled={isSaving || !settingsDirty}>
                          {isSaving ? (
                            <>
                              <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                              Saving...
                            </>
                          ) : (
                            'Save changes'
                          )}
                        </Button>
                        {saveStatus ? (
                          <span className={`save-status is-${saveStatus.type === 'success' ? 'success' : 'error'}`} role="status">
                            {saveStatus.message}
                          </span>
                        ) : settingsDirty && (
                          <span className="save-status">Unsaved changes</span>
                        )}
                      </div>
                    </>
                  )}
                </Form>
              </Card.Body>
            </Card>
          </Tab>
        )}

        {office.hasCarParkPlan && (
          <Tab eventKey="carParking" title="Car parking plans">
            {canEdit && (
              <Modal show={showAddPlanModal} onHide={closePlanModal} centered>
                <Modal.Header closeButton>
                  <Modal.Title>{editingPlan ? 'Edit car park plan' : 'Add car park plan'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  {createPlanError && (
                    <Alert variant="danger" dismissible onClose={() => setCreatePlanError(null)}>
                      {createPlanError}
                    </Alert>
                  )}

                  <Form>
                    <Form.Group className="mb-3" controlId="carParkPlanName">
                      <Form.Label>Name</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter car park plan name"
                        value={newPlanName}
                        onChange={(e) => setNewPlanName(e.target.value)}
                        disabled={isCreatingPlan}
                      />
                    </Form.Group>

                    <SvgFileField
                      id="carParkMap"
                      label="Car park map"
                      value={newPlanMap}
                      onChange={setNewPlanMap}
                      disabled={isCreatingPlan}
                    />
                  </Form>
                </Modal.Body>
                <Modal.Footer>
                  <Button variant="outline-secondary" onClick={closePlanModal} disabled={isCreatingPlan}>
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleSaveCarParkPlan}
                    disabled={isCreatingPlan || !newPlanName.trim()}
                  >
                    {isCreatingPlan ? (
                      <>
                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                        Saving...
                      </>
                    ) : (
                      editingPlan ? 'Save changes' : 'Add plan'
                    )}
                  </Button>
                </Modal.Footer>
              </Modal>
            )}

            <Row>
              <Col>
                <Card className="shadow-sm">
                  <Card.Header className="bg-light d-flex flex-wrap justify-content-between align-items-center gap-2">
                    <h5 className="mb-0">Car park plans ({carParkPlans.length})</h5>
                    <div className="d-flex align-items-center gap-3">
                      {planSaved && <span className="save-status is-success" role="status">Car park plan saved</span>}
                      {canEdit && (
                        <Button variant="primary" size="sm" onClick={openAddPlanModal}>
                          Add plan
                        </Button>
                      )}
                    </div>
                  </Card.Header>
                  <Card.Body className="p-0">
                    {carParkPlansLoading ? (
                      <LoadingState label="Loading car park plans" />
                    ) : carParkPlansError ? (
                      <ErrorState inCard message="Could not load the car park plans." onRetry={fetchCarParkPlans} />
                    ) : carParkPlans.length === 0 ? (
                      <div className="p-4 text-center text-muted">
                        No car park plans for this office yet
                      </div>
                    ) : (
                      <Table responsive className="mb-0">
                        <thead className="table-light">
                          <tr>
                            <th className="d-none d-md-table-cell">ID</th>
                            <th>Name</th>
                            <th>Map</th>
                            {canEdit && <th className="text-end"><span className="visually-hidden">Actions</span></th>}
                          </tr>
                        </thead>
                        <tbody>
                          {carParkPlans.map((plan) => (
                            <tr key={plan.id}>
                              <td className="align-middle d-none d-md-table-cell">{plan.id}</td>
                              <td className="align-middle"><strong>{plan.name}</strong></td>
                              <td className="align-middle">
                                {plan.carParkMap ? (
                                  <SvgPreview svg={plan.carParkMap} alt={`Map of ${plan.name}`} maxHeight={48} />
                                ) : plan.carParkPlan ? (
                                  <span className="text-muted" title={plan.carParkPlan}>Map uploaded</span>
                                ) : (
                                  <span className="text-muted">No map</span>
                                )}
                              </td>
                              {canEdit && (
                                <td className="align-middle text-end">
                                  <Button variant="outline-secondary" size="sm" onClick={() => openEditPlanModal(plan)}>
                                    Edit
                                  </Button>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Tab>
        )}
      </Tabs>
    </Container>
  );
};

export default OfficeDetail;
